/**
 * Toast — lightweight ephemeral feedback for user actions.
 *
 * Mount <ToastProvider> ONCE at the app root. Anywhere below it,
 * call useToast() to get { showToast(message, variant?) }.
 *
 * Implementation choices:
 * - Custom framer-motion (already installed) instead of @radix-ui/
 *   react-toast (would be a new dep + heavier). Keeps Phase 6's
 *   new-dependency budget at "just one" (Radix ContextMenu).
 * - Toasts mount in a fixed bottom-right stack. Newest at the bottom.
 * - Auto-dismiss after 3 seconds. Click X to dismiss earlier.
 * - aria-live="polite" so screen readers announce the message.
 *
 * Data Room v2 Phase 6 (2026-05-26).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, X as XIcon, AlertCircle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastVariant = 'default' | 'success' | 'error' | 'info';

interface ToastEntry {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  showToast: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const AUTO_DISMISS_MS = 3000;

/** Hook returning { showToast }. Must be called inside <ToastProvider>. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const showToast = useCallback((message: string, variant: ToastVariant = 'default') => {
    setToasts((prev) => [...prev, { id: Date.now() + Math.random(), message, variant }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* Fixed toast stack — bottom-right. role + aria-live so screen
          readers announce arrivals. */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none"
      >
        <AnimatePresence initial={false}>
          {toasts.map((t) => (
            <ToastItem key={t.id} entry={t} onDismiss={dismiss} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({
  entry,
  onDismiss,
}: {
  entry: ToastEntry;
  onDismiss: (id: number) => void;
}) {
  useEffect(() => {
    const t = window.setTimeout(() => onDismiss(entry.id), AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [entry.id, onDismiss]);

  const Icon = entry.variant === 'success'
    ? CheckCircle2
    : entry.variant === 'error'
      ? AlertCircle
      : Info;

  const variantClasses = entry.variant === 'success'
    ? 'border-emerald-200 text-emerald-700 bg-emerald-50'
    : entry.variant === 'error'
      ? 'border-red-200 text-red-700 bg-red-50'
      : 'border-slate-200 text-slate-700 bg-white';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 10, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      role="status"
      className={cn(
        'pointer-events-auto inline-flex items-center gap-2 px-3 py-2 rounded-lg border shadow-md text-sm min-w-[220px] max-w-md',
        variantClasses,
      )}
    >
      <Icon size={16} className="flex-shrink-0" />
      <span className="flex-1">{entry.message}</span>
      <button
        type="button"
        onClick={() => onDismiss(entry.id)}
        aria-label="Dismiss"
        className="opacity-60 hover:opacity-100 transition-opacity flex-shrink-0"
      >
        <XIcon size={14} />
      </button>
    </motion.div>
  );
}
